import { useCallback } from 'react';

import { DoctorSimple } from '@dreryk/egabinet-personnel-frontend-api';

import { BindingCreator, createSingleInterpolatedTranslationKey, useTranslate } from '@dreryk/react-common';
import { Multiselect, Text } from '@dreryk/react-components';

import { filterPractitionersWithMedicalLicenceNumber } from '~legacy.personnel';

import { patientTranslationKeys } from '../../translations';

type PrescriptionsHistoryPractitionersFilterProps = {
   readonly doctors: DoctorSimple[];
   readonly register: BindingCreator<{ practitioners: DoctorSimple[] }, 'practitioners'>;
};

const getDoctorName = (doctor: DoctorSimple): string => `${doctor.lastName} ${doctor.firstName}`;

export const PrescriptionsHistoryPractitionersFilter = (
   props: PrescriptionsHistoryPractitionersFilterProps
): JSX.Element => {
   const translate = useTranslate();
   const practitionersWithMedicalLicenceNumber = filterPractitionersWithMedicalLicenceNumber(props.doctors);

   const triggerLabelExtractor = useCallback(
      (selected: DoctorSimple[]): string => {
         if (!selected.length) {
            return translate(patientTranslationKeys.prescriptionsHistoryFilters.chooseSpecialists);
         }

         if (selected.length === practitionersWithMedicalLicenceNumber.length) {
            return translate(patientTranslationKeys.prescriptionsHistoryFilters.allSpecialists);
         }

         if (selected.length === 1) {
            return getDoctorName(selected[0]);
         }

         return translate(
            createSingleInterpolatedTranslationKey(
               patientTranslationKeys.prescriptionsHistoryFilters.chosenSpecialists,
               'doctorsCount',
               selected.length
            )
         );
      },
      [practitionersWithMedicalLicenceNumber, translate]
   );

   return (
      <Multiselect<DoctorSimple>
         testId={'practitioners'}
         triggerLabelExtractor={triggerLabelExtractor}
         itemKeyGenerator={'practitionerId'}
         controller={props.register('practitioners')}
         width='250px'
         placeholder={patientTranslationKeys.prescriptionsHistoryFilters.chooseSpecialists}
         compact
      >
         <>
            <Multiselect.SelectAll<DoctorSimple>
               testId={'selectAll'}
               items={practitionersWithMedicalLicenceNumber}
               selectAllLabel={patientTranslationKeys.prescriptionsHistoryFilters.allSpecialists}
            />
            <Multiselect.List<DoctorSimple>
               items={practitionersWithMedicalLicenceNumber}
               keyExtractor={'practitionerId'}
            >
               {(doctor) => (
                  <Multiselect.Item
                     item={doctor}
                     label={<Text testId={`doctor-${doctor.practitionerId}`}>{getDoctorName(doctor)}</Text>}
                     testId={doctor.practitionerId}
                  />
               )}
            </Multiselect.List>
         </>
      </Multiselect>
   );
};
